import { Stack, useFocusEffect, useRouter } from 'expo-router';
import { useDepositCashFlow } from 'features/moveMoney/providers/DepositCashFlowContextProvider';
import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AppState } from 'react-native';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import type { LocationAccess } from 'shared/hooks/maps/useLocationServices';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';

interface LocationServicesErrorScreenProps {
  access: LocationAccess;
  hasPermission: boolean;
  requestPermission: () => Promise<boolean>;
  checkLocationServices: () => Promise<void>;
}

export default function LocationServicesErrorScreen({
  access: _access,
  hasPermission,
  requestPermission,
  checkLocationServices,
}: LocationServicesErrorScreenProps) {
  const router = useRouter();
  const { t: translatedText } = useTranslation('moveMoney');
  const { flowName } = useDepositCashFlow();
  const { segmentClient } = useAnalyticsConfiguration();

  const hasRequestedPermission = useRef(false);

  useViewDidFocusScreenAnalyticsEvent({
    key: '/move-money/deposit-cash/barcode/location-required-error',
    customAttributes: {
      error_code: '',
      user_error_message: 'locations_off_for_barcode',
      error_category: 'cash_deposit_errors',
      subcategory: 'cash_deposit_barcode',
      flow_name: flowName,
    },
  });

  const handleClicksBackAnalytics = usePressableAnalytics({
    analyticsId: 'clicksBack',
    customAttributes: {
      error_code: '',
      user_error_message: 'locations_off_for_barcode',
      error_category: 'cash_deposit_errors',
      subcategory: 'cash_deposit_barcode',
      flow_name: flowName,
    },
  });

  // On mount: request permission if not already granted (triggers native popup)
  useEffect(() => {
    if (hasPermission || hasRequestedPermission.current) {
      return;
    }

    hasRequestedPermission.current = true;

    void (async () => {
      try {
        const granted = await requestPermission();
        void segmentClient.logEvent(
          'User Grants Native Location Services Access',
          {
            category: 'deposit_cash',
            source_name: 'cash_deposit_location_map',
            subcategory: 'cash_deposit_barcode',
            location_access: granted ? 'Granted' : 'Denied',
            flow_name: flowName,
          },
        );
      } catch (error) {
        logErrorWithContext(error, {
          featureArea: FeatureArea.PAYMENTS,
          responsibleTeam: ResponsibleTeam.PAYMENTS_TRANSFER,
          severity: ErrorSeverity.LOW,
          additionalContext: {
            scope: 'LocationServicesErrorScreen.requestPermission',
          },
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When app returns from background, re-check location services status and request permission if needed
  useFocusEffect(
    useCallback(() => {
      const subscription = AppState.addEventListener(
        'change',
        async (nextState) => {
          if (nextState === 'active') {
            await checkLocationServices();

            // If user returns from Settings without granting permission, request again
            // This handles the "Never" → "Ask Every Time" scenario
            if (!hasPermission) {
              hasRequestedPermission.current = false;
            }
          }
        },
      );

      return () => subscription.remove();
    }, [checkLocationServices, hasPermission]),
  );

  const handleGoBack = () => {
    handleClicksBackAnalytics();

    void segmentClient.logEvent('Clicks Back On Location Services Error', {
      error_code: '',
      user_error_message: 'locations_off_for_barcode',
      error_category: 'cash_deposit_errors',
      subcategory: 'cash_deposit_barcode',
      flow_name: flowName,
    });

    if (router.canGoBack()) {
      router.back();
    }
  };

  return (
    <>
      <Stack.Screen
        options={{
          headerShown: false,
        }}
      />
      <ErrorScreen
        screenType={ErrorScreenType.Error}
        messageHeadline={translatedText(
          'depositCash.barcode.locationServices.title',
        )}
        messageBody={translatedText(
          'depositCash.barcode.locationServices.subtitle',
        )}
        primaryButton={{
          title: translatedText(
            'depositCash.barcode.locationServices.backButton',
          ),
          onPress: handleGoBack,
        }}
        insetBottomSafeArea
      />
    </>
  );
}
